/**
 * roomStore — 联机房间状态管理
 *
 * 职责：
 *  - 管理 Socket.io 连接
 *  - 房间创建/加入/重连
 *  - 游戏状态广播（作为房主）/ 接收（非房主）
 *  - 掉线玩家标记同步
 */

import { create } from 'zustand';
import { io, Socket } from 'socket.io-client';
import type { RoomSnapshot, RoomStoreState, OnlineMode } from '../types';
import { useGameStore } from './gameStore';

// 服务器地址：生产环境用同域名，开发环境用本地 3001
const SERVER_URL =
  typeof window !== 'undefined' && window.location.hostname !== 'localhost'
    ? `${window.location.protocol}//${window.location.hostname}`
    : 'http://localhost:3001';

interface RoomActions {
  // 连接管理
  connect: () => void;
  disconnect: () => void;

  // 房间操作
  createRoom: (playerName: string, characterId: string) => Promise<{ ok: boolean; roomId?: string; error?: string }>;
  joinRoom: (roomId: string, playerName: string, characterId: string) => Promise<{ ok: boolean; reconnected?: boolean; error?: string; snapshot?: RoomSnapshot }>;
  startGame: () => void;

  // 游戏命令（服务端权威：客户端发送意图，服务端执行并广播状态）
  sendRollCommand: () => void;
  sendDiceChoiceCommand: (steps: number) => void;
  sendBuyCommand: (buy: boolean) => void;
  sendUpgradeCommand: (upgrade: boolean) => void;
  sendBailCommand: () => void;
  sendJailDoublesCommand: () => void;
  sendCellEventCloseCommand: () => void;
  sendUseCardCommand: (cardId: string, targetPlayerName?: string, extra?: any) => void;
  sendRevealIdentityCommand: () => void;
  sendSellPropertyCommand: (cellId: number) => void;
  sendRefreshStateCommand: () => void;

  // 重置
  reset: () => void;

  // 内部 socket 引用
  _socket: Socket | null;
  _isHost: boolean;
}

type RoomStore = RoomStoreState & RoomActions;

// ─── localStorage 缓存键 ──────────────────────────
export const GAME_CACHE_KEY = 'monopoly_online_game';

export interface GameCache {
  roomId: string;
  myPlayerName: string;
  myCharacterId: string;
  phase: string;
  lastActiveAt: number;
}

export function saveGameCache(cache: GameCache) {
  try {
    localStorage.setItem(GAME_CACHE_KEY, JSON.stringify(cache));
  } catch { /* ignore quota errors */ }
}

export function loadGameCache(): GameCache | null {
  try {
    const raw = localStorage.getItem(GAME_CACHE_KEY);
    if (!raw) return null;
    const cache = JSON.parse(raw) as GameCache;
    // 缓存超过 2 小时视为失效
    if (Date.now() - cache.lastActiveAt > 2 * 60 * 60 * 1000) {
      localStorage.removeItem(GAME_CACHE_KEY);
      return null;
    }
    return cache;
  } catch {
    return null;
  }
}

export function clearGameCache() {
  try {
    localStorage.removeItem(GAME_CACHE_KEY);
  } catch { /* ignore */ }
}

/** 重连后强制将本地玩家的 isBot 设为 false、disconnected 设为 false */
function fixLocalPlayerState(myPlayerName: string) {
  if (!myPlayerName) return;
  const currentPlayers = useGameStore.getState().players;
  if (!currentPlayers?.length) return;
  const myName = myPlayerName.trim().toLowerCase();
  let changed = false;
  const updatedPlayers = currentPlayers.map(p => {
    if (p.name.trim().toLowerCase() !== myName) return p;
    if (!p.isBot && !p.disconnected) return p; // 已正确，跳过
    changed = true;
    return { ...p, disconnected: false, disconnectedAt: null, isBot: false };
  });
  if (changed) {
    useGameStore.setState({ players: updatedPlayers });
    console.log(`[room] fixed local player "${myPlayerName}" isBot state after reconnect`);
  }
}

/** 屏蔽未暴露的隐藏身份（客户端安全层）*/
export function maskIdentities(gameState: any, myPlayerName: string | null): any {
  if (!gameState?.players || !myPlayerName) return gameState;
  const myName = myPlayerName.trim().toLowerCase();
  const maskedPlayers = gameState.players.map((p: any) => {
    const isMe = p.name?.trim().toLowerCase() === myName;
    if (isMe || p.identityRevealed) return p; // 自己或已暴露 → 保留
    return { ...p, identityId: '' }; // 未暴露 → 屏蔽
  });
  // 🔧 服务端日志格式归一化：服务端用 msg 字段，客户端显示用 message
  const normalizedLogs = gameState.logs?.map?.((l: any) => ({
    ...l,
    message: l.message || l.msg || '',
  })) || gameState.logs;
  return { ...gameState, players: maskedPlayers, logs: normalizedLogs };
}

export const useRoomStore = create<RoomStore>((set, get) => ({
  // ─── 初始状态 ─────────────────────────────────────
  mode: 'offline' as OnlineMode,
  roomId: null,
  myPlayerName: null,
  roomSnapshot: null,
  connected: false,
  connecting: false,
  error: null,
  _socket: null,
  _isHost: false,

  // ─── 连接 ─────────────────────────────────────────
  connect: () => {
    if (get()._socket?.connected) return;

    set({ connecting: true, error: null });

    const socket = io(SERVER_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1500,
    });

    socket.on('connect', () => {
      set({ connected: true, connecting: false });

      // 重连后自动重新加入之前的房间
      const { mode, roomId, myPlayerName } = get();
      if (mode === 'online' && roomId && myPlayerName) {
        console.log(`[room] auto-rejoining room ${roomId} as ${myPlayerName}`);
        socket.emit('room:join', { roomId, playerName: myPlayerName }, (res: any) => {
          if (res?.ok) {
            console.log(`[room] auto-rejoin OK, reconnected=${res.reconnected}`);
            if (res.reconnected && res.snapshot) {
              set({ roomSnapshot: res.snapshot });
              // 如果游戏进行中，用服务器状态恢复本地
              if (res.snapshot.gameState) {
                // 🔧 屏蔽未暴露的身份数据
                const maskedState = maskIdentities(res.snapshot.gameState, myPlayerName);
                useGameStore.setState(maskedState);
                // 🔧 重连后强制将本地玩家的 isBot 设为 false、disconnected 设为 false
                //    解决 room:snapshot/game:state merge 逻辑中 p.isBot 残留导致玩家一直被视为 Bot
                fixLocalPlayerState(myPlayerName);
              }
            }
          } else {
            console.warn(`[room] auto-rejoin failed: ${res?.error}`);
          }
        });
      }
    });

    socket.on('disconnect', (_reason) => {
      set({ connected: false });
    });

    socket.on('reconnect_attempt', () => {
      set({ connected: false, connecting: true });
    });

    socket.on('connect_error', (err) => {
      set({ connecting: false, error: `连接失败: ${err.message}` });
    });

    // ── 房间快照（玩家列表变更、掉线等）───────────────
    socket.on('room:snapshot', (snapshot: RoomSnapshot) => {
      set({ roomSnapshot: snapshot });

      // 同步掉线状态到 gameStore
      const gameState = useGameStore.getState();
      if (gameState.phase === 'playing' || gameState.phase === 'gameOver') {
        const myName = get().myPlayerName?.trim().toLowerCase();
        const updatedPlayers = gameState.players.map(p => {
          const slot = snapshot.players.find(
            s => s.name.trim().toLowerCase() === p.name.trim().toLowerCase()
          );
          if (!slot) return p;
          const isMe = myName && p.name.trim().toLowerCase() === myName;
          return {
            ...p,
            disconnected: slot.disconnected,
            disconnectedAt: slot.disconnectedAt,
            // 🔧 本地玩家：以 slot.disconnected 为准（不掉线就是人类，不是 Bot）
            //    其他玩家：掉线时按 Bot 托管
            isBot: isMe ? slot.disconnected : (slot.disconnected || p.isBot),
          };
        });
        useGameStore.setState({ players: updatedPlayers });
      }
    });

    // ── 玩家重连通知 ──────────────────────────────────
    socket.on('player:reconnected', ({ playerName }: { playerName: string }) => {
      // 取消该玩家的 Bot 托管
      const gameState = useGameStore.getState();
      if (gameState.phase === 'playing') {
        const updatedPlayers = gameState.players.map(p => {
          if (p.name.trim().toLowerCase() !== playerName.trim().toLowerCase()) return p;
          // 找原始 isBot 状态（重连的都是真人）
          return { ...p, disconnected: false, disconnectedAt: null, isBot: false };
        });
        useGameStore.setState({ players: updatedPlayers });
      }
    });

    // ── 游戏状态同步（服务端权威，全量状态）──
    socket.on('game:state', ({ state }: { state: any }) => {
      // 诊断日志：追踪状态同步
      console.log('[STATE] game:state received: turnPhase=', state.turnPhase, 'currentPlayerIndex=', state.currentPlayerIndex,
        'phase=', state.phase,
        'diceResult=', state.diceResult,
        'positions=', state.players?.map((p: any) => `${p.name}:${p.position}(bot=${p.isBot})`).join(', '));
      
      // 服务端是唯一权威源：直接接受完整状态，无需合并 roomSnapshot
      // server/index.js 的 serializeGameState() 已经包含正确的 isBot/disconnected 标记

      // 保留本地 broadcast：若新的 broadcast 为空但本地有旧值则不覆盖
      const currentGame = useGameStore.getState();
      if (!state.broadcast && currentGame.broadcast && currentGame.phase === state.phase) {
        state.broadcast = currentGame.broadcast;
      }

      // 保留本地通知（不在 state 中，不被覆盖）
      if (currentGame.comebackNotification) state.comebackNotification = currentGame.comebackNotification;
      if (currentGame.landChangeNotification) state.landChangeNotification = currentGame.landChangeNotification;

      // 🔧 屏蔽未暴露的身份数据（客户端安全层）
      // 只有身份已暴露的玩家 或 本地玩家自己的身份才显示真实 identityId
      const myName = get().myPlayerName;
      if (state.players && myName) {
        state = maskIdentities(state, myName); // 同时完成身份屏蔽 + 日志格式归一化
      }

      useGameStore.setState(state);

      // 游戏结束：清除缓存
      if (state.phase === 'gameOver') {
        clearGameCache();
      }

      // 更新缓存（含角色ID，确保重连后头像正确）
      const { roomId, myPlayerName } = get();
      if (roomId && myPlayerName) {
        const gs = useGameStore.getState();
        const myPlayer = gs.players?.find(p =>
          p.name.trim().toLowerCase() === myPlayerName.trim().toLowerCase()
        );
        const myCharacterId = myPlayer?.characterId || '';
        saveGameCache({ roomId, myPlayerName, myCharacterId, phase: state.phase, lastActiveAt: Date.now() });
      }
    });

    // ── 回合变更通知 ────────────────────────────────
    socket.on('notify:turn_change', (data: any) => {
      // 服务端权威的回合切换通知
      // 客户端用此更新 currentPlayerIndex / turnPhase 等
      const gs = useGameStore.getState();
      if (gs.phase === 'playing') {
        useGameStore.setState({
          currentPlayerIndex: data.nextPlayerIndex,
          turnNumber: data.turnNumber,
          roundNumber: data.roundNumber,
          // 🔧 使用 ?? 而非 ||：null 表示过期清除，不应 fallback 到旧值
          activeGlobalEvent: 'activeGlobalEvent' in data ? data.activeGlobalEvent : gs.activeGlobalEvent,
          turnPhase: 'rolling',
          diceResult: null,
          // 清除上个玩家的 UI 状态
          broadcast: null,
        });
      }
    });

    // ── 回合恢复通知（重连时服务端发送）──────────────
    socket.on('notify:turn_resume', (data: any) => {
      const gs = useGameStore.getState();
      if (gs.phase === 'playing') {
        useGameStore.setState({
          currentPlayerIndex: data.nextPlayerIndex,
          turnPhase: data.turnPhase || 'rolling',
          diceResult: data.diceResult || null,
          turnNumber: data.turnNumber,
          roundNumber: data.roundNumber,
          // 🔧 null 表示无事件，不应 fallback 到旧值
          activeGlobalEvent: 'activeGlobalEvent' in data ? data.activeGlobalEvent : gs.activeGlobalEvent,
        });
      }
    });

    // ── 玩家移动通知（用于触发其他玩家的动画）─────────
    socket.on('notify:player_moved', (data: any) => {
      // 此通知携带移动信息（from/to/steps），由 GameBoard 的动画系统处理
      // 数据已通过 game:state 更新到 store，此处仅作日志/调试
      console.log(`[room] player_moved: ${data.playerName} ${data.from}→${data.to} (${data.steps} steps)`);
    });

    // ── 房间开始（非房主接收）─────────────────────────
    socket.on('room:started', ({ snapshot }: { snapshot: RoomSnapshot }) => {
      if (get()._isHost) return; // 房主已自行初始化
      set({ roomSnapshot: snapshot });
      // 服务端权威模式：snapshot.gameState 已包含完整初始状态
      if (snapshot.gameState) {
        // game:state 紧随其后也会发送，但此处先用 snapshot 确保 GameBoard 挂载
        // 🔧 屏蔽未暴露的身份数据
        const maskedState = maskIdentities(snapshot.gameState, get().myPlayerName);
        useGameStore.setState({ ...maskedState, phase: 'playing' });
      } else {
        // 兜底：仅设 phase
        useGameStore.setState({ phase: 'playing' });
      }
    });

    set({ _socket: socket });
  },

  disconnect: () => {
    get()._socket?.disconnect();
    set({ _socket: null, connected: false, mode: 'offline', roomId: null });
  },

  // ─── 创建房间 ──────────────────────────────────────
  createRoom: (playerName, characterId) => {
    return new Promise((resolve) => {
      const { _socket, connect } = get();
      if (!_socket?.connected) connect();

      const trimmedName = playerName.trim();

      // 等待连接就绪后发送
      const doCreate = () => {
        get()._socket!.emit('room:create', { playerName: trimmedName, characterId }, (res: any) => {
          if (res.ok) {
            set({
              mode: 'online',
              roomId: res.roomId,
              myPlayerName: trimmedName,
              roomSnapshot: res.snapshot,
              _isHost: true,
            });
            // 缓存房间信息，用于自动重连（保存角色ID以修复重连后头像错误）
            saveGameCache({ roomId: res.roomId, myPlayerName: trimmedName, myCharacterId: characterId, phase: 'waiting', lastActiveAt: Date.now() });
          }
          resolve(res);
        });
      };

      if (get()._socket?.connected) {
        doCreate();
      } else {
        get()._socket?.once('connect', doCreate);
      }
    });
  },

  // ─── 加入房间 ──────────────────────────────────────
  joinRoom: (roomId, playerName, characterId) => {
    return new Promise((resolve) => {
      const { connect } = get();
      connect();

      const trimmedName = playerName.trim();

      const doJoin = () => {
        get()._socket!.emit('room:join', { roomId: roomId.toUpperCase(), playerName: trimmedName, characterId }, (res: any) => {
          if (res.ok) {
            set({
              mode: 'online',
              roomId: res.roomId,
              myPlayerName: trimmedName,
              roomSnapshot: res.snapshot,
              _isHost: false,
            });

            // 若重连且游戏已在进行，用服务器的 gameState 恢复本地
            if (res.reconnected && res.snapshot?.gameState) {
              // 🔧 屏蔽未暴露的身份数据
              const maskedState = maskIdentities(res.snapshot.gameState, trimmedName);
              useGameStore.setState(maskedState);
              // 🔧 重连后强制将本地玩家的 isBot 设为 false、disconnected 设为 false
              fixLocalPlayerState(trimmedName);
            }
            // 缓存房间信息（含角色ID），用于自动重连
            saveGameCache({
              roomId: res.roomId,
              myPlayerName: trimmedName,
              myCharacterId: characterId,
              phase: res.snapshot?.gameState?.phase || res.snapshot?.phase || 'waiting',
              lastActiveAt: Date.now(),
            });
          }
          resolve(res);
        });
      };

      if (get()._socket?.connected) {
        doJoin();
      } else {
        get()._socket?.once('connect', doJoin);
      }
    });
  },

  // ─── 开始游戏（房主专属）──────────────────────────
  startGame: () => {
    const { _socket, roomId } = get();
    if (!_socket || !roomId) return;
    _socket.emit('room:start', { roomId });
  },

  // ─── 游戏命令（服务端权威）─────────────────────────
  // 客户端发送意图，服务端执行规则并广播 game:state
  sendRollCommand: () => {
    const { _socket, roomId } = get();
    if (!_socket) { console.warn('[sendRollCommand] _socket is null'); return; }
    if (!roomId) { console.warn('[sendRollCommand] roomId is null'); return; }
    console.log('[ROLLCMD] emitting game:roll to server, roomId=', roomId, 'socket.id=', _socket.id);
    _socket.emit('game:roll', { roomId });
  },

  sendDiceChoiceCommand: (steps) => {
    const { _socket, roomId } = get();
    if (!_socket || !roomId) return;
    _socket.emit('game:dice_choice', { roomId, steps });
  },

  sendBuyCommand: (buy) => {
    const { _socket, roomId } = get();
    if (!_socket || !roomId) return;
    _socket.emit('game:cell_action', { roomId, action: buy ? 'buy' : 'skip_buy' });
  },

  sendUpgradeCommand: (upgrade) => {
    const { _socket, roomId } = get();
    if (!_socket || !roomId) return;
    _socket.emit('game:cell_action', { roomId, action: upgrade ? 'upgrade' : 'skip_upgrade' });
  },

  sendBailCommand: () => {
    const { _socket, roomId } = get();
    if (!_socket || !roomId) return;
    _socket.emit('game:bail', { roomId });
  },

  sendJailDoublesCommand: () => {
    const { _socket, roomId } = get();
    if (!_socket || !roomId) return;
    _socket.emit('game:jail_doubles', { roomId });
  },

  sendCellEventCloseCommand: () => {
    const { _socket, roomId } = get();
    if (!_socket || !roomId) return;
    // 根据当前阶段发送对应的关闭 action
    const turnPhase = useGameStore.getState().turnPhase;
    const actionMap: Record<string, string> = {
      reverseShop: 'reverse_shop_close',
      fateEvent: 'fate_close',
    };
    const action = actionMap[turnPhase] || 'event_close';
    _socket.emit('game:cell_action', { roomId, action });
  },

  sendUseCardCommand: (cardId, targetPlayerName, extra) => {
    const { _socket, roomId } = get();
    if (!_socket || !roomId) return;
    _socket.emit('game:use_card', { roomId, cardId, targetPlayerName, extra });
  },

  sendRevealIdentityCommand: () => {
    const { _socket, roomId } = get();
    if (!_socket || !roomId) return;
    _socket.emit('game:reveal_identity', { roomId });
  },

  sendSellPropertyCommand: (cellId) => {
    const { _socket, roomId } = get();
    if (!_socket || !roomId) return;
    _socket.emit('game:sell_property', { roomId, cellId });
  },

  sendRefreshStateCommand: () => {
    const { _socket, roomId } = get();
    if (!_socket || !roomId) return;
    // 卡住时重新向服务器请求最新状态
    _socket.emit('game:refresh', { roomId });
  },

  // ─── 重置 ─────────────────────────────────────────
  reset: () => {
    clearGameCache();
    get()._socket?.disconnect();
    set({
      mode: 'offline',
      roomId: null,
      myPlayerName: null,
      roomSnapshot: null,
      connected: false,
      connecting: false,
      error: null,
      _socket: null,
      _isHost: false,
    });
  },
}));
